import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import TextInput from "../../Common/FormElements/TextInput/TextInput";
import Select from "../../Common/FormElements/Select/Select";
import CalendarInput from "../../Common/FormElements/CalendarInput/CalendarInput";
import Button from "../../Common/FormElements/Button/Button";
import ConfirmModal from "../../Common/Modal/ConfirmModal";
import QuotePill, { formatEuro } from "./QuotePill";
import {
  addSalePayment,
  deleteSalePayment,
  updateSalePayment,
} from "../../../REDUX/actions/appProjects";
import { openCenteredModal } from "../../../REDUX/actions/centeredModal";

// API app-order/utils/saleRules PAYMENT_METHODS (raw i18n labels).
export const PAYMENT_METHODS = [
  { code: "cash", name: "Cash" },
  { code: "cheque", name: "Cheque" },
  { code: "transfer", name: "Bank transfer" },
  { code: "card", name: "Bank card" },
  { code: "financing", name: "Financing" },
  { code: "other", name: "Other" },
];

const EMPTY = { id: null, amount: "", date: null, method: null, note: "" };

/**
 * Client sheet › Paiements: what the customer paid the store (deposit, then
 * balance). The same follow-up as Poseidon's payments, without any invoice —
 * it is between the store and its customer. The customer sees each payment
 * in the app.
 */
const ProjectPayments = ({ sale, onChange }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Remounts the form: CalendarInput / Select keep their own copy of the value.
  const [formKey, setFormKey] = useState(0);
  const reset = () => {
    setDraft(EMPTY);
    setFormKey((key) => key + 1);
  };
  const methods = PAYMENT_METHODS.map(({ code, name }) => ({ code, name: t(name) }));
  const cancelled = sale.state === "cancelled";
  const customer = sale.customer?.name ?? t("Client");

  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const valid = Number(draft.amount) > 0 && draft.date;

  const save = async () => {
    setSaving(true);
    const payment = {
      amount: Number(draft.amount),
      date: moment(draft.date).format("YYYY-MM-DD"),
      method: draft.method?.code ?? "other",
      note: draft.note,
    };
    const data = await dispatch(
      draft.id
        ? updateSalePayment(sale.id, draft.id, payment, t)
        : addSalePayment(sale.id, payment, t),
    );
    setSaving(false);
    if (!data) return;
    onChange(data);
    reset();
  };

  const edit = (payment) =>
    setDraft({
      id: payment.id,
      amount: String(payment.amount),
      date: new Date(`${payment.date}T12:00:00`),
      method: methods.find((m) => m.code === payment.methodCode) ?? null,
      note: payment.note ?? "",
    });

  const remove = (payment) =>
    dispatch(
      openCenteredModal(
        <ConfirmModal
          label={t("this payment")}
          onConfirm={async () => {
            const data = await dispatch(deleteSalePayment(sale.id, payment.id, t));
            if (data) onChange(data);
          }}
        />,
      ),
    );

  const tiles = [
    ["Order total incl. VAT", formatEuro(sale.total)],
    [
      t("Deposit {{rate}} %", { rate: sale.depositRate }),
      formatEuro(sale.deposit),
      sale.depositPaid ? ["green", "Settled"] : ["orange", "To pay"],
    ],
    ["Already paid", formatEuro(sale.paid)],
    ["Remaining to pay", formatEuro(sale.balanceDue)],
  ];

  return (
    <div className="px-5 py-4">
      <div className="row g-3 mb-4">
        {tiles.map(([label, value, pill]) => (
          <div className="col-3" key={label}>
            <div className="p-3 br10 b-1-lightGrey h-100">
              <p className="f13 text-brightGrey mb-1">{t(label)}</p>
              <p className="f18 sBold text-ultraBlack mb-0">{value}</p>
              {pill && (
                <div className="mt-2">
                  <QuotePill tone={pill[0]}>{t(pill[1])}</QuotePill>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {cancelled && (
        <div
          className="p-3 br10 mb-4 f14 sBold"
          style={{ background: "rgba(204,31,8,0.08)", color: "#cc1f08" }}
        >
          {sale.refundedAmount > 0
            ? t("Order cancelled — {{amount}} to refund to {{customer}}", {
                amount: formatEuro(sale.refundedAmount),
                customer,
              })
            : t("Order cancelled")}
        </div>
      )}

      <div className="alignH bg-dimGrey px-4 py-3 gap-2 f14 sBold br5">
        <span className="w15">{t("Payment date")}</span>
        <span className="w15">{t("Payment Amount")}</span>
        <span className="w20">{t("Payment method")}</span>
        <span className="w15">{t("Type")}</span>
        <span className="w25">{t("Note")}</span>
        <span className="w10" />
      </div>
      {(sale.payments ?? []).length === 0 && (
        <p className="f14 text-brightGrey px-4 py-3 mb-0">{t("No payment yet")}</p>
      )}
      {(sale.payments ?? []).map((payment) => (
        <div
          key={payment.id}
          className="alignH px-4 py-3 gap-2 f14 b-1-bottom-softGrey"
        >
          <span className="w15">{moment(payment.date).format("DD/MM/YYYY")}</span>
          <span className="w15 sBold">{formatEuro(payment.amount)}</span>
          <span className="w20">
            {t(PAYMENT_METHODS.find((m) => m.code === payment.methodCode)?.name ?? "Other")}
          </span>
          <span className="w15">
            {t(payment.kind === "deposit" ? "Deposit" : "Balance")}
          </span>
          <span className="w25 text-brightGrey">{payment.note || "-"}</span>
          <span className="w10 d-flex gap-3 justify-content-end">
            {!cancelled && (
              <>
                <button
                  type="button"
                  className="border-0 bg-transparent text-brightGrey"
                  onClick={() => edit(payment)}
                >
                  <i className="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  className="border-0 bg-transparent text-red-600"
                  onClick={() => remove(payment)}
                >
                  <i className="bi bi-trash" />
                </button>
              </>
            )}
          </span>
        </div>
      ))}

      {!cancelled && (draft.id || sale.balanceDue > 0) && (
        <div className="mt-4 p-3 br10 b-1-lightGrey" key={formKey}>
          <p className="f14 sBold text-ultraBlack mb-3">
            {t(draft.id ? "Edit the payment" : "Add a Payment")}
          </p>
          <div className="row g-3 align-items-end">
            <div className="col-2">
              <TextInput
                euro
                type="number"
                min="0"
                placeholder={t("Payment Amount")}
                value={draft.amount}
                callback={(e) => set({ amount: e.target.value })}
              />
            </div>
            <div className="col-3">
              <CalendarInput
                withMinDate={false}
                placeholder={t("Payment date")}
                value={draft.date}
                callback={(e) => set({ date: e.value ?? null })}
              />
            </div>
            <div className="col-3">
              <Select
                appendToSelf
                placeholder={t("Payment method")}
                options={methods}
                value={draft.method}
                callback={(e) => set({ method: e.value ?? null })}
              />
            </div>
            <div className="col-4">
              <TextInput
                placeholder={t("Note")}
                value={draft.note}
                callback={(e) => set({ note: e.target.value })}
              />
            </div>
          </div>
          <div className="d-flex justify-content-end gap-3 mt-3">
            {draft.id && (
              <Button variant="outline" onClick={reset}>
                {t("Cancel")}
              </Button>
            )}
            <Button
              loading={saving}
              disabled={!valid}
              onClick={saving || !valid ? undefined : save}
            >
              {t(draft.id ? "Save" : "Add the payment")}
            </Button>
          </div>
        </div>
      )}

      <p className="f13 text-brightGrey mt-3 mb-0">
        {t(
          "{{name}} sees these payments in the app. They are between the store and its customer: no invoice is generated.",
          { name: customer },
        )}
      </p>
    </div>
  );
};

export default ProjectPayments;
